import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle2,
  Music2,
  Download,
  RefreshCcw,
  ArrowLeft,
  Search,
  Radio,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

const COLORS = ["#D4AF37", "#FFB800", "#F3C93E", "#A1A1AA", "#71717A", "#968032"];
const REFRESH_MS = 30000;

const TYPE_OPTIONS = [
  { v: "all", l: "All types" },
  { v: "mechanical", l: "Mechanical" },
  { v: "performance", l: "Performance" },
  { v: "streaming", l: "Streaming" },
  { v: "sync", l: "Sync" },
];
const STATUS_OPTIONS = [
  { v: "all", l: "All statuses" },
  { v: "pending", l: "Pending" },
  { v: "received", l: "Received" },
  { v: "paid_out", l: "Paid out" },
];

const fmt = (n) =>
  "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function Kpi({ label, value, Icon, accent, tid }) {
  return (
    <div
      data-testid={tid}
      className={`bg-[#0a0a0a] p-6 md:p-8 ${accent ? "border-l-2 border-l-[var(--primary)]" : ""}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
        <Icon size={16} className={accent ? "text-[var(--primary)]" : "text-zinc-600"} strokeWidth={1.5} />
      </div>
      <div
        className={
          "font-heading font-black text-3xl md:text-4xl tracking-tighter " +
          (accent ? "text-[var(--primary)]" : "")
        }
      >
        {value}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const cls =
    status === "received"
      ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10"
      : status === "paid_out"
      ? "text-[var(--primary)] border-[var(--primary)]/40 bg-[var(--primary)]/10"
      : "text-amber-300 border-amber-500/40 bg-amber-500/10";
  return (
    <span className={`inline-flex px-2 py-1 text-[10px] font-mono uppercase tracking-widest border ${cls}`}>
      {String(status || "pending").replace("_", " ")}
    </span>
  );
}

export default function Royalties() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [placementsCount, setPlacementsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [query, setQuery] = useState("");
  const timerRef = useRef(null);

  const load = async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const [s, r, p] = await Promise.all([
        api.get("/me/royalties/summary"),
        api.get("/me/royalties"),
        api.get("/me/placements"),
      ]);
      setSummary(s.data);
      setRows(r.data || []);
      setPlacementsCount((p.data || []).length);
      setLastUpdated(new Date());
    } catch (e) {
      if (!silent) toast.error(e.response?.data?.detail || "Could not load royalties.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(() => load({ silent: true }), REFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, []);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (typeFilter !== "all" && r.type !== typeFilter) return false;
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (query) {
        const q = query.toLowerCase();
        if (
          !String(r.source || "").toLowerCase().includes(q) &&
          !String(r.period || "").toLowerCase().includes(q) &&
          !String(r.type || "").toLowerCase().includes(q) &&
          !String(r.notes || "").toLowerCase().includes(q)
        )
          return false;
      }
      return true;
    });
  }, [rows, typeFilter, statusFilter, query]);

  const filteredTotal = useMemo(
    () => filtered.reduce((acc, r) => acc + Number(r.amount || 0), 0),
    [filtered]
  );

  const byPeriodData = useMemo(() => {
    const m = {};
    filtered.forEach((r) => {
      m[r.period] = (m[r.period] || 0) + Number(r.amount || 0);
    });
    return Object.entries(m)
      .map(([period, amount]) => ({ period, amount }))
      .sort((a, b) => (a.period > b.period ? 1 : -1));
  }, [filtered]);

  const byTypeData = useMemo(() => {
    const m = {};
    filtered.forEach((r) => {
      m[r.type] = (m[r.type] || 0) + Number(r.amount || 0);
    });
    return Object.entries(m).map(([name, value]) => ({ name, value }));
  }, [filtered]);

  const bySourceData = useMemo(() => {
    const m = {};
    filtered.forEach((r) => {
      m[r.source] = (m[r.source] || 0) + Number(r.amount || 0);
    });
    return Object.entries(m)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [filtered]);

  const payouts = useMemo(() => filtered.filter((r) => r.status === "paid_out"), [filtered]);

  const exportCsv = () => {
    const header = ["Type", "Source", "Period", "Status", "Amount", "Notes", "Logged"];
    const lines = filtered.map((r) =>
      [
        r.type,
        r.source,
        r.period,
        r.status,
        r.amount,
        (r.notes || "").replace(/"/g, '""'),
        r.created_at ? new Date(r.created_at).toISOString() : "",
      ]
        .map((v) => `"${String(v ?? "")}"`)
        .join(",")
    );
    const csv = [header.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `equantum-royalties-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported.");
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="royalties-page">
      {/* Sub-header */}
      <div className="border-b border-white/10 bg-[#070707]">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--primary)] mb-2 flex items-center gap-2">
              <Radio size={10} className="animate-pulse" /> / live royalty dashboard
            </div>
            <h1 className="font-heading font-black text-3xl md:text-5xl tracking-tighter">
              Your royalties & payouts
            </h1>
            <p className="text-zinc-500 text-sm mt-2">
              {rows.length} entries tracked · auto-refreshes every 30s
              {lastUpdated && (
                <span className="ml-2 text-zinc-600">
                  · last update {lastUpdated.toLocaleTimeString()}
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Link to="/dashboard" className="btn-ghost" data-testid="back-to-portal">
              <ArrowLeft size={14} /> Portal
            </Link>
            <button
              onClick={() => load({ silent: true })}
              className="btn-ghost"
              data-testid="royalties-refresh"
            >
              <RefreshCcw size={14} className={refreshing ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              onClick={exportCsv}
              disabled={!filtered.length}
              className="btn-primary disabled:opacity-40"
              data-testid="royalties-export-csv"
            >
              <Download size={14} /> Export CSV
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-10 space-y-10">
        {/* KPI cards */}
        <section className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-white/10">
          <Kpi
            label="Total tracked"
            value={summary ? fmt(summary.total) : "—"}
            Icon={DollarSign}
            accent
            tid="kpi-total"
          />
          <Kpi
            label="Received"
            value={summary ? fmt(summary.received) : "—"}
            Icon={TrendingUp}
            tid="kpi-received"
          />
          <Kpi
            label="Pending"
            value={summary ? fmt(summary.pending) : "—"}
            Icon={Clock}
            tid="kpi-pending"
          />
          <Kpi
            label="Paid out"
            value={summary ? fmt(summary.paid_out) : "—"}
            Icon={CheckCircle2}
            tid="kpi-paid-out"
          />
          <Kpi label="Placements" value={placementsCount} Icon={Music2} tid="kpi-placements" />
        </section>

        {/* Filters */}
        <section className="bg-[#0a0a0a] border border-white/10 p-4 md:p-5 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search source, period, notes…"
              data-testid="royalties-search"
              className="w-full bg-black/60 border border-white/10 pl-9 pr-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:border-[var(--primary)]/60 outline-none font-mono"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            data-testid="royalties-filter-type"
            className="bg-black/60 border border-white/10 px-3 py-2.5 text-xs text-white font-mono uppercase tracking-widest focus:border-[var(--primary)]/60 outline-none"
          >
            {TYPE_OPTIONS.map((o) => (
              <option key={o.v} value={o.v}>
                {o.l}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="royalties-filter-status"
            className="bg-black/60 border border-white/10 px-3 py-2.5 text-xs text-white font-mono uppercase tracking-widest focus:border-[var(--primary)]/60 outline-none"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.v} value={o.v}>
                {o.l}
              </option>
            ))}
          </select>
          <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 ml-auto">
            Filtered:{" "}
            <span className="text-[var(--primary)]">{fmt(filteredTotal)}</span>{" "}
            <span className="text-zinc-600">· {filtered.length} rows</span>
          </div>
        </section>

        {/* Charts */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-white/10">
          <div className="lg:col-span-2 bg-[#0a0a0a] p-6 md:p-8">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              // earnings by period
            </div>
            <h3 className="font-heading font-bold text-xl mt-2 mb-6">Royalty timeline</h3>
            <div className="h-[300px]">
              {byPeriodData.length === 0 ? (
                <EmptyBlock loading={loading} />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byPeriodData}>
                    <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" />
                    <XAxis dataKey="period" stroke="#71717A" fontSize={11} />
                    <YAxis stroke="#71717A" fontSize={11} />
                    <Tooltip
                      contentStyle={{
                        background: "#0a0a0a",
                        border: "1px solid rgba(255,255,255,0.1)",
                        color: "#fff",
                        fontSize: 12,
                      }}
                      formatter={(v) => fmt(v)}
                    />
                    <Bar dataKey="amount" fill="#D4AF37" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="bg-[#0a0a0a] p-6 md:p-8">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              // mix by type
            </div>
            <h3 className="font-heading font-bold text-xl mt-2 mb-4">Breakdown</h3>
            <div className="h-[220px]">
              {byTypeData.length === 0 ? (
                <EmptyBlock loading={loading} />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={byTypeData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={44}
                      outerRadius={80}
                      paddingAngle={2}
                    >
                      {byTypeData.map((entry, i) => (
                        <Cell key={entry.name} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        background: "#0a0a0a",
                        border: "1px solid rgba(255,255,255,0.1)",
                        color: "#fff",
                        fontSize: 12,
                      }}
                      formatter={(v) => fmt(v)}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
            <ul className="mt-4 space-y-2">
              {byTypeData.map((d, i) => (
                <li key={d.name} className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-2 text-zinc-400 capitalize">
                    <span className="w-2 h-2" style={{ background: COLORS[i % COLORS.length] }} />
                    {d.name}
                  </span>
                  <span className="font-mono text-zinc-300">{fmt(d.value)}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* By source */}
        <section className="bg-[#0a0a0a] border border-white/10 p-6 md:p-8">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            // top sources
          </div>
          <h3 className="font-heading font-bold text-xl mt-2 mb-6">Where your money is coming from</h3>
          {bySourceData.length === 0 ? (
            <EmptyBlock loading={loading} compact />
          ) : (
            <ul className="space-y-3">
              {bySourceData.map((s) => {
                const pct = filteredTotal ? Math.max(2, Math.round((s.value / filteredTotal) * 100)) : 0;
                return (
                  <li key={s.name} className="group" data-testid={`source-row-${s.name}`}>
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <span className="text-zinc-200">{s.name}</span>
                      <span className="font-mono text-[var(--primary)]">
                        {fmt(s.value)}{" "}
                        <span className="text-zinc-500">· {pct}%</span>
                      </span>
                    </div>
                    <div className="h-2 bg-white/5">
                      <div
                        className="h-2 bg-[var(--primary)] transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Payout timeline */}
        <section className="bg-[#0a0a0a] border border-white/10">
          <div className="p-6 md:p-8 border-b border-white/10 flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                // payouts
              </div>
              <h3 className="font-heading font-bold text-xl mt-2">Payout timeline</h3>
            </div>
            <div className="text-xs font-mono text-zinc-500">
              {payouts.length} payouts · {fmt(payouts.reduce((a, r) => a + Number(r.amount || 0), 0))}
            </div>
          </div>
          {payouts.length === 0 ? (
            <div className="px-6 md:px-8 py-10 text-center text-zinc-600 text-sm">
              No payouts cleared yet. We mark entries as "Paid out" the moment the money hits.
            </div>
          ) : (
            <ol className="divide-y divide-white/5">
              {payouts.map((p) => (
                <li
                  key={p.id}
                  data-testid={`payout-row-${p.id}`}
                  className="px-6 md:px-8 py-4 flex items-center justify-between gap-6"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <CheckCircle2 size={16} className="text-[var(--primary)] flex-shrink-0" strokeWidth={1.5} />
                    <div className="min-w-0">
                      <div className="text-sm text-white truncate">
                        {p.source}{" "}
                        <span className="text-zinc-500 font-mono text-xs">· {p.type}</span>
                      </div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                        {p.period}{" "}
                        {p.created_at && (
                          <span className="text-zinc-600">
                            · logged {new Date(p.created_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="font-mono text-[var(--primary)] font-semibold whitespace-nowrap">
                    {fmt(p.amount)}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </section>

        {/* Full table */}
        <section className="bg-[#0a0a0a] border border-white/10">
          <div className="p-6 md:p-8 border-b border-white/10 flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                // ledger
              </div>
              <h3 className="font-heading font-bold text-xl mt-2">All royalty entries</h3>
            </div>
            <div className="text-xs text-zinc-500">
              {filtered.length} of {rows.length}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="royalties-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
                  <th className="px-6 md:px-8 py-4">Type</th>
                  <th className="py-4">Source</th>
                  <th className="py-4">Period</th>
                  <th className="py-4">Status</th>
                  <th className="py-4">Logged</th>
                  <th className="py-4 text-right pr-6 md:pr-8">Amount</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 md:px-8 py-10 text-center text-zinc-600 text-sm">
                      {loading
                        ? "Loading…"
                        : rows.length === 0
                        ? "No entries yet. Your consultant populates this as money is tracked and collected."
                        : "No entries match these filters."}
                    </td>
                  </tr>
                )}
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-6 md:px-8 py-4 capitalize text-zinc-200">{r.type}</td>
                    <td className="py-4 text-zinc-300">{r.source}</td>
                    <td className="py-4 font-mono text-xs text-zinc-400">{r.period}</td>
                    <td className="py-4">
                      <StatusPill status={r.status} />
                    </td>
                    <td className="py-4 font-mono text-xs text-zinc-500">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="py-4 pr-6 md:pr-8 text-right font-mono text-[var(--primary)]">
                      {fmt(r.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
              {filtered.length > 0 && (
                <tfoot>
                  <tr className="border-t border-white/10">
                    <td
                      colSpan={5}
                      className="px-6 md:px-8 py-4 text-right text-[10px] uppercase tracking-widest font-mono text-zinc-500"
                    >
                      Filtered total
                    </td>
                    <td className="py-4 pr-6 md:pr-8 text-right font-mono text-base text-[var(--primary)] font-semibold">
                      {fmt(filteredTotal)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}

function EmptyBlock({ loading, compact }) {
  return (
    <div
      className={
        "h-full flex items-center justify-center text-zinc-600 " +
        (compact ? "text-xs py-10" : "text-sm")
      }
    >
      {loading ? "Loading…" : "No data in this view yet."}
    </div>
  );
}
